import { Fragment, type ReactNode } from 'react';

import {
	FormattedBlock,
	FormattedContent,
	addFormatter,
} from 'atoms/formatted-content/formatter';

const allowedInlineFormatters = [
	'md:headings',
	'md:formatting',
	'md:anchors',
	'md:code-inline',
];

addFormatter('md:lists', {
	selector: /(^\t{0,4}(-|\*|\d+\.)\s.*)/gim,
	ignoreMatchSplit: true,
	decorator: (guid: string, match: string): ReactNode => {
		const isOrdered = match.includes('1. '); // Might be a bit naïve but...
		const rows = match.split('\n');

		const items: any = {};

		let indent: number = 0;
		let parent: string = '';

		const trimRow = (row: string) =>
			`${row}`
				.trim()
				.replace(/^(-|\*|\d+\.)/, '')
				.trim();

		rows.forEach((row: string, index: number) => {
			const nextIndent = row.split(/\t/)?.length - 1;

			const item = trimRow(row);

			if (nextIndent !== indent) {
				parent = trimRow(rows[index - 1] as string);
				indent = nextIndent;

				if (nextIndent === 0) {
					parent = '';
				}
			}

			if (!parent) {
				items[item] = [];
			} else {
				const next = items[parent] ?? [];

				next.push(item);
				items[parent] = next;
			}
		});

		if (isOrdered) {
			return (
				<ol key={guid}>
					{Object.entries(items).map(([key, subItems]) => (
						<li>
							<FormattedBlock
								raw={key}
								allowedFormatters={allowedInlineFormatters}
							/>
							{(subItems as string[]).length > 0 && (
								<ol>
									{(subItems as string[]).map((item: string) => (
										<li>
											<FormattedBlock
												raw={item}
												allowedFormatters={allowedInlineFormatters}
											/>
										</li>
									))}
								</ol>
							)}
						</li>
					))}
				</ol>
			);
		}

		return (
			<ul key={guid}>
				{Object.entries(items).map(([key, subItems], n) => (
					<li key={n}>
						<FormattedBlock
							raw={key}
							allowedFormatters={allowedInlineFormatters}
						/>
						{(subItems as string[]).length > 0 && (
							<ul>
								{(subItems as string[]).map((item: string) => (
									<li>
										<FormattedBlock
											raw={item}
											allowedFormatters={allowedInlineFormatters}
										/>
									</li>
								))}
							</ul>
						)}
					</li>
				))}
			</ul>
		);
	},
});

addFormatter('md:blockquote', {
	selector: /(^>\s(?!\[!(?:NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]).*)/gim,
	ignoreMatchSplit: true,
	decorator: (guid: string, match: string): ReactNode => {
		const rows = match
			.split('\n')
			.map((row: string) => row.replace('>', '').trim())
			.join('\n');

		return (
			<blockquote key={guid}>
				<FormattedContent
					raw={rows}
					allowedFormatters={allowedInlineFormatters}
				/>
			</blockquote>
		);
	},
});

addFormatter('md:ingress', {
	selector: /(^%\s.*)/gim,
	ignoreMatchSplit: true,
	decorator: (guid: string, match: string): ReactNode => {
		const rows = match
			.split('\n')
			.map((row: string) => row.replace('%', '').trim())
			.join('\n');

		return (
			<article key={guid} data-ingress>
				<FormattedContent
					raw={rows}
					allowedFormatters={allowedInlineFormatters}
				/>
			</article>
		);
	},
});

addFormatter('md:headings', {
	selector: /^(#{1,5} .*)$/gi,
	decorator: (guid: string, match: string): ReactNode => {
		const re = /^(?<headingSize>#{1,5}) (?<headingContent>.*)/gi;

		const { headingSize, headingContent } = re.exec(match)?.groups || {};

		const heading = (
			<FormattedContent
				inline
				raw={headingContent ?? ''}
				allowedFormatters={allowedInlineFormatters}
			/>
		);

		switch (headingSize?.length) {
			case 1:
				return (
					<h1 data-heading key={guid}>
						{heading}
					</h1>
				);
			case 2:
				return (
					<h2 data-heading key={guid}>
						{heading}
					</h2>
				);
			case 3:
				return (
					<h3 data-heading key={guid}>
						{heading}
					</h3>
				);
			case 4:
				return (
					<h4 data-heading key={guid}>
						{heading}
					</h4>
				);
			case 5:
				return (
					<h5 data-heading key={guid}>
						{heading}
					</h5>
				);
			default:
				return <Fragment key={guid}>{heading}</Fragment>;
		}
	},
});

addFormatter('md:formatting', {
	selector: /(__.+?__|\*\*.+?\*\*|~~.+?~~|\+\+.+?\+\+)/gim,
	decorator: (guid: string, match: string): ReactNode => {
		const string = match.slice(2, -2);

		let type = '';

		// @NOTE Hacky validation, but it works...

		if (match.startsWith('__') && match.endsWith('__')) {
			type = 'em';
		} else if (match.startsWith('**') && match.endsWith('**')) {
			type = 'strong';
		} else if (match.startsWith('~~') && match.endsWith('~~')) {
			type = 'del';
		} else if (match.startsWith('++') && match.endsWith('++')) {
			type = 'ins';
		}

		if (!type) {
			return <FormattedBlock key={guid} raw={match} />;
		}

		switch (type) {
			case 'em':
				return <em key={guid}>{string}</em>;
			case 'strong':
				return <strong key={guid}>{string}</strong>;
			case 'ins':
				return <ins key={guid}>{string}</ins>;
			case 'del':
				return <del key={guid}>{string}</del>;
			default:
				return <FormattedBlock key={guid} raw={match} />;
		}
	},
});

addFormatter('md:anchors', {
	selector: /(\[.*?\]\(.*?\))/gim,
	decorator: (guid: string, match: string): ReactNode => {
		const re = /\[(?<name>(.*?))\]\((?<path>(.*?))\)/gim;
		const { name, path } = re.exec(match)?.groups || {
			name: 'Link',
			path: '#',
		};

		match.match(/\[(.*?)\]\((.*?)\)/gim);

		if (path?.startsWith('/') || path?.startsWith('#')) {
			return <a href={path}>{name}</a>;
		}

		return (
			<a
				key={guid}
				data-cursor-magnetic
				href={path}
				target="_blank"
				rel="noreferrer">
				{name}
			</a>
		);
	},
});

addFormatter('md:divider', {
	selector: /(-{3,})/g,
	decorator: (_, match: string): ReactNode => {
		if (match.length >= 5) {
			return <hr data-spacing />;
		}
		return <hr />;
	},
});

addFormatter('md:code-block', {
	selector: /^[`]{3}(?:\w+)?\n([\s\S]*?)[`]{3}$/gim,
	decorator: (guid: string, match: string, { subject }): ReactNode => {
		let language: string = 'tsx';

		if (typeof subject === 'string') {
			const $match = /^[`]{3}(?<language>(?:\w+)?)/i.exec(subject);

			if ($match?.groups?.language) {
				language = $match.groups.language;
			}
		}

		return (
			<pre data-code-block data-code-block-lang={language}>
				{match.replace(/^\s+|\s+$/g, '')}
			</pre>
		);
	},
});

addFormatter('md:code-inline', {
	selector: /[`]{1}(.+?)[`]{1}/gim,
	decorator: (guid: string, match: string): ReactNode => {
		return <code data-code-block-lang="tsx">{match}</code>;
	},
});
